import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, withCtx as _withCtx, createVNode as _createVNode, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, withModifiers as _withModifiers, vShow as _vShow, withDirectives as _withDirectives, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-682e10db"),n=n(),_popScopeId(),n)
const _hoisted_1 = {
  id: "show-datastore-tables",
  class: "d-flex flex-fill list-page"
}
const _hoisted_2 = ["id"]
const _hoisted_3 = { class: "datastore-table-desc" }
const _hoisted_4 = { class: "d-flex flex-row flex-wrap align-items-center gap-2" }
const _hoisted_5 = {
  key: 0,
  class: "datastore-hint"
}
const _hoisted_6 = { key: 1 }
const _hoisted_7 = { class: "d-flex flex-row justify-content-between" }
const _hoisted_8 = { class: "col d-flex flex-row align-items-center" }
const _hoisted_9 = { class: "d-flex align-items-center" }
const _hoisted_10 = { class: "btn-label" }
const _hoisted_11 = { class: "btn-text" }
const _hoisted_12 = { class: "d-flex flex-row align-items-center justify-content-center" }
const _hoisted_13 = { class: "pagination-wrapper mt-1" }
const _hoisted_14 = { class: "margin-top-20 d-flex flex-column" }
const _hoisted_15 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("span", { class: "color-red" }, "*", -1))
const _hoisted_16 = { class: "margin-top-20 d-flex flex-column" }
const _hoisted_17 = { class: "d-flex justify-content-between align-items-center" }
const _hoisted_18 = { class: "d-flex align-items-center gap-2" }
const _hoisted_19 = { class: "margin-top-20 d-flex flex-column" }
const _hoisted_20 = { class: "d-flex align-items-center gap-1" }
const _hoisted_21 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("span", { class: "color-red" }, "*", -1))
const _hoisted_22 = { class: "loader-div-40" }
const _hoisted_23 = ["src"]

import { computed, onMounted, ref } from "vue";
import {
  ElButton,
  ElCard,
  ElCheckbox,
  ElDialog,
  ElDivider,
  ElDropdown,
  ElDropdownItem,
  ElDropdownMenu,
  ElInput,
  ElTable,
  ElTableColumn,
  ElTag,
  ElTooltip,
} from "element-plus";
import { TYPE } from "vue-toastification";
import { showAlert, showConfirm } from "@/core/popup";
import i18n from "@/core/i18n/i18n";
import { router } from "@/core/router";
import { ASSETS_BASE_URL, DEFAULT_LOGO } from "@/core/logo";
import OneTegPagination from "@/common/components/OneTegPagination.vue";
import { DataStoreTable } from "@/datastore/types";
import {
  canDataStore,
  canDeleteTable,
  createTable,
  dataStoreMockMode,
  deleteTable,
  listTables,
  updateTable,
} from "@/datastore/services/datastore.service";
import { mockPrivileges } from "@/datastore/services/datastore.mock";

const SHARED_WITH_LIMIT = 2;


export default /*#__PURE__*/_defineComponent({
  __name: 'DataStoreTables',
  setup(__props) {

const { t } = i18n.global;

/** Names shown in the grid's shared-with cell before it becomes a count. */
const tables = ref<DataStoreTable[]>([]);
const search = ref("");
const loading = ref(false);
const currentPage = ref(1);
const pageSize = ref(25);
const createDialogVisible = ref(false);
const newTableName = ref("");
const newTableDescription = ref("");
const renameDialogVisible = ref(false);
const renameValue = ref("");
const renameTarget = ref<DataStoreTable | null>(null);

/**
 * listTables returns the whole result set, so the page is cut here rather than
 * asked for — the service contract stays as it is.
 */
const pagedTables = computed(() => {
  const start = (currentPage.value - 1) * pageSize.value;
  return tables.value.slice(start, start + pageSize.value);
});

function formatCount(n: number): string {
  return n.toLocaleString("en-US");
}

function togglePrivilege(key: string, value: boolean) {
  mockPrivileges[key] = value;
}

async function loadTables() {
  loading.value = true;
  try {
    tables.value = await listTables(search.value);
    // Deleting the last table on the last page would otherwise leave the user
    // paged past the end, looking at an empty grid.
    const lastPage = Math.max(
      1,
      Math.ceil(tables.value.length / pageSize.value)
    );
    if (currentPage.value > lastPage) {
      currentPage.value = lastPage;
    }
  } catch (e: any) {
    showAlert(TYPE.ERROR, e.message ?? t("Common.UnknownError"));
  } finally {
    loading.value = false;
  }
}

/**
 * OneTegPagination binds `:page-size` one-way onto ElPagination, so its
 * `update:pageSize` never fires — the new size only arrives on `size-change`.
 * Every other list page reads it the same way.
 */
function onPageSizeChange(size: number) {
  pageSize.value = size;
  currentPage.value = 1;
}

/** The house search guard: one character is never a query worth running. */
function onSearchChanged() {
  if (search.value.length === 0 || search.value.length > 2) {
    currentPage.value = 1;
    loadTables();
  }
}

function openTable(table: DataStoreTable, withImport = false) {
  router.push({
    path: `/datastore/edit/${table.id}`,
    query: withImport ? { import: "1" } : undefined,
  });
}

/**
 * Element passes (row, column, event) to @row-click, so the row handler is kept
 * separate from openTable — binding openTable directly would land `column` in
 * its `withImport` argument and open every table with the import wizard.
 */
function goToEditPage(row: DataStoreTable) {
  openTable(row);
}

/**
 * "Create a table directly from a CSV import" (design §14) is the same two steps the API takes —
 * a table, then an import that creates the fields from the header — with the wizard opened for the
 * user instead of left for them to find. The table is created first either way, because the import
 * needs something to import into.
 *
 * @param withImport open the editor with the import wizard already showing
 */
async function onCreateTable(withImport = false) {
  const name = newTableName.value.trim();
  if (!name) {
    showAlert(TYPE.WARNING, t("DataStore.NameRequired"));
    return;
  }
  try {
    const created = await createTable(name, newTableDescription.value.trim());
    createDialogVisible.value = false;
    newTableName.value = "";
    newTableDescription.value = "";
    showAlert(TYPE.SUCCESS, t("DataStore.TableCreated", { name }));
    openTable(created, withImport);
  } catch (e: any) {
    showAlert(TYPE.ERROR, e.message ?? t("Common.UnknownError"));
  }
}

function openRename(table: DataStoreTable) {
  renameTarget.value = table;
  renameValue.value = table.name;
  renameDialogVisible.value = true;
}

async function onRenameTable() {
  const target = renameTarget.value;
  const name = renameValue.value.trim();
  if (!target) return;
  if (!name) {
    showAlert(TYPE.WARNING, t("DataStore.NameRequired"));
    return;
  }
  try {
    await updateTable(target.id, { name });
    renameDialogVisible.value = false;
    showAlert(TYPE.SUCCESS, t("DataStore.TableRenamed"));
    await loadTables();
  } catch (e: any) {
    showAlert(TYPE.ERROR, e.message ?? t("Common.UnknownError"));
  }
}

async function confirmDelete(table: DataStoreTable) {
  const confirmed = await showConfirm(
    "warning",
    t("DataStore.DeleteTable"),
    t("DataStore.DeleteTableWarning", {
      name: table.name,
      rows: formatCount(table.rowCount),
    })
  );
  if (!confirmed) return;
  try {
    await deleteTable(table.id);
    showAlert(TYPE.WARNING, t("DataStore.TableDeleted", { name: table.name }));
    await loadTables();
  } catch (e: any) {
    showAlert(TYPE.ERROR, e.message ?? t("Common.UnknownError"));
  }
}

onMounted(loadTables);

return (_ctx: any,_cache: any) => {
  const _component_font_awesome_icon = _resolveComponent("font-awesome-icon")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_unref(ElCard), { class: "card border-0 oneTeg-card oneTeg-card-body" }, {
      default: _withCtx(() => [
        _createVNode(_unref(ElTable), {
          data: pagedTables.value,
          class: "oneTeg-table",
          height: "97%",
          "row-class-name": "oneTeg-table-row-permission",
          "empty-text": loading.value ? ' ' : _unref(t)('Common.NoData'),
          onRowClick: goToEditPage
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(ElTableColumn), {
              label: _unref(t)('Common.Name'),
              "min-width": "220"
            }, {
              default: _withCtx((scope) => [
                _createElementVNode("strong", {
                  id: 'datastore-open-' + scope.$index
                }, _toDisplayString(scope.row.name), 9, _hoisted_2),
                _createElementVNode("div", _hoisted_3, _toDisplayString(scope.row.description), 1)
              ]),
              _: 1
            }, 8, ["label"]),
            _createVNode(_unref(ElTableColumn), {
              label: _unref(t)('DataStore.Fields'),
              width: "90"
            }, {
              default: _withCtx((scope) => [
                _createTextVNode(_toDisplayString(scope.row.fields.length), 1)
              ]),
              _: 1
            }, 8, ["label"]),
            _createVNode(_unref(ElTableColumn), {
              label: _unref(t)('DataStore.Rows'),
              width: "100"
            }, {
              default: _withCtx((scope) => [
                _createTextVNode(_toDisplayString(formatCount(scope.row.rowCount)), 1)
              ]),
              _: 1
            }, 8, ["label"]),
            _createVNode(_unref(ElTableColumn), {
              label: _unref(t)('DataStore.SharedWith'),
              "min-width": "150"
            }, {
              default: _withCtx((scope) => [
                _createElementVNode("div", _hoisted_4, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(scope.row.sharedWith.slice(
                  0,
                  SHARED_WITH_LIMIT
                ), (share) => {
                    return (_openBlock(), _createBlock(_unref(ElTag), {
                      key: share.organizationId,
                      size: "small",
                      type: "success"
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(share.name), 1)
                      ]),
                      _: 2
                    }, 1024))
                  }), 128)),
                  (scope.row.sharedWith.length > SHARED_WITH_LIMIT)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_5, _toDisplayString(_unref(t)("DataStore.AndMoreOrganizations", {
                    count: scope.row.sharedWith.length - SHARED_WITH_LIMIT,
                  })), 1))
                    : _createCommentVNode("", true),
                  (scope.row.sharedWith.length === 0)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_6, "—"))
                    : _createCommentVNode("", true)
                ])
              ]),
              _: 1
            }, 8, ["label"]),
            _createVNode(_unref(ElTableColumn), {
              label: _unref(t)('DataStore.Modified'),
              width: "160"
            }, {
              default: _withCtx((scope) => [
                _createTextVNode(_toDisplayString(scope.row.modifiedAt), 1)
              ]),
              _: 1
            }, 8, ["label"]),
            _createVNode(_unref(ElTableColumn), { width: "360px" }, {
              header: _withCtx(() => [
                _createElementVNode("div", _hoisted_7, [
                  _createVNode(_unref(ElInput), {
                    id: "datastore-search",
                    modelValue: search.value,
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((search).value = $event)),
                    size: "mini",
                    class: "oneTeg-search-input max-w-170",
                    placeholder: _unref(t)('DataStore.SearchTables'),
                    onInput: onSearchChanged
                  }, null, 8, ["modelValue", "placeholder"]),
                  _createVNode(_unref(ElDivider), {
                    direction: "vertical",
                    class: "v-divider-stretch",
                    "aria-hidden": "true"
                  }),
                  _createElementVNode("div", _hoisted_8, [
                    (_unref(dataStoreMockMode))
                      ? (_openBlock(), _createBlock(_unref(ElDropdown), {
                          key: 0,
                          trigger: "click",
                          "hide-on-click": false
                        }, {
                          dropdown: _withCtx(() => [
                            _createVNode(_unref(ElDropdownMenu), null, {
                              default: _withCtx(() => [
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(mockPrivileges), (value, key) => {
                                  return (_openBlock(), _createBlock(_unref(ElDropdownItem), { key: key }, {
                                    default: _withCtx(() => [
                                      _createVNode(_unref(ElCheckbox), {
                                        id: 'datastore-priv-' + key,
                                        "model-value": value,
                                        onChange: (v) => togglePrivilege(key, v)
                                      }, {
                                        default: _withCtx(() => [
                                          _createTextVNode(_toDisplayString(key), 1)
                                        ]),
                                        _: 2
                                      }, 1032, ["id", "model-value", "onChange"])
                                    ]),
                                    _: 2
                                  }, 1024))
                                }), 128))
                              ]),
                              _: 1
                            })
                          ]),
                          default: _withCtx(() => [
                            _createVNode(_unref(ElButton), {
                              id: "datastore-priv-toggle",
                              class: "no-border btn-oneTeg margin-right-10",
                              size: "mini",
                              type: "primary",
                              "aria-label": _unref(t)('DataStore.MockPrivileges')
                            }, {
                              default: _withCtx(() => [
                                _createVNode(_component_font_awesome_icon, { icon: "shield-alt" })
                              ]),
                              _: 1
                            }, 8, ["aria-label"])
                          ]),
                          _: 1
                        }))
                      : _createCommentVNode("", true),
                    (_unref(canDataStore)('TABLE.CREATE'))
                      ? (_openBlock(), _createBlock(_unref(ElButton), {
                          key: 1,
                          id: "datastore-create-button",
                          class: "no-border btn-oneTeg2-table-create btn-labeled",
                          size: "mini",
                          type: "primary",
                          onClick: _cache[1] || (_cache[1] = ($event: any) => (createDialogVisible.value = true))
                        }, {
                          default: _withCtx(() => [
                            _createElementVNode("span", _hoisted_9, [
                              _createElementVNode("span", _hoisted_10, [
                                _createVNode(_component_font_awesome_icon, { icon: "plus" })
                              ]),
                              _createElementVNode("span", _hoisted_11, _toDisplayString(_unref(t)("DataStore.CreateTable")), 1)
                            ])
                          ]),
                          _: 1
                        }))
                      : _createCommentVNode("", true)
                  ])
                ])
              ]),
              default: _withCtx((scope) => [
                _createElementVNode("div", _hoisted_12, [
                  _createVNode(_unref(ElDropdown), null, {
                    dropdown: _withCtx(() => [
                      _createVNode(_unref(ElDropdownMenu), null, {
                        default: _withCtx(() => [
                          _createVNode(_unref(ElDropdownItem), {
                            id: 'datastore-open-btn-' + scope.$index,
                            onClick: ($event: any) => (openTable(scope.row))
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(_unref(t)("DataStore.Open")), 1)
                            ]),
                            _: 2
                          }, 1032, ["id", "onClick"]),
                          (_unref(canDataStore)('TABLE.EDIT'))
                            ? (_openBlock(), _createBlock(_unref(ElDropdownItem), {
                                key: 0,
                                id: 'datastore-rename-btn-' + scope.$index,
                                onClick: ($event: any) => (openRename(scope.row))
                              }, {
                                default: _withCtx(() => [
                                  _createTextVNode(_toDisplayString(_unref(t)("DataStore.Rename")), 1)
                                ]),
                                _: 2
                              }, 1032, ["id", "onClick"]))
                            : _createCommentVNode("", true),
                          (_unref(canDeleteTable)(scope.row))
                            ? (_openBlock(), _createBlock(_unref(ElDropdownItem), {
                                key: 1,
                                id: 'datastore-delete-btn-' + scope.$index,
                                onClick: ($event: any) => (confirmDelete(scope.row))
                              }, {
                                default: _withCtx(() => [
                                  _createTextVNode(_toDisplayString(_unref(t)("Common.Delete")), 1)
                                ]),
                                _: 2
                              }, 1032, ["id", "onClick"]))
                            : _createCommentVNode("", true)
                        ]),
                        _: 2
                      }, 1024)
                    ]),
                    default: _withCtx(() => [
                      _createVNode(_unref(ElButton), {
                        class: "no-border btn-oneTeg show-on-row-hover max-w-42-5",
                        size: "mini",
                        type: "primary",
                        id: 'datastore-cog-button-' + scope.$index,
                        "aria-label": _unref(t)('Common.Actions'),
                        onClick: _cache[2] || (_cache[2] = _withModifiers(() => {}, ["stop"]))
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_font_awesome_icon, { icon: "cog" })
                        ]),
                        _: 2
                      }, 1032, ["id", "aria-label"])
                    ]),
                    _: 2
                  }, 1024)
                ])
              ]),
              _: 1
            })
          ]),
          _: 1
        }, 8, ["data", "empty-text"]),
        _createElementVNode("div", _hoisted_13, [
          _createVNode(OneTegPagination, {
            "page-sizes": [10, 25, 50, 100],
            "page-size": pageSize.value,
            "onUpdate:pageSize": _cache[3] || (_cache[3] = ($event: any) => ((pageSize).value = $event)),
            "current-page": currentPage.value,
            "onUpdate:currentPage": _cache[4] || (_cache[4] = ($event: any) => ((currentPage).value = $event)),
            total: tables.value.length,
            background: true,
            small: true,
            layout: 'total, sizes, prev, pager, next',
            onSizeChange: onPageSizeChange
          }, null, 8, ["page-size", "current-page", "total"])
        ])
      ]),
      _: 1
    }),
    _createVNode(_unref(ElDialog), {
      modelValue: createDialogVisible.value,
      "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((createDialogVisible).value = $event)),
      title: _unref(t)('DataStore.CreateTable'),
      width: "440px",
      "custom-class": "datastore-dialog",
      "append-to-body": "",
      "destroy-on-close": "",
      "close-on-click-modal": false
    }, {
      footer: _withCtx(() => [
        _createElementVNode("div", _hoisted_17, [
          _createVNode(_unref(ElButton), {
            id: "datastore-create-import",
            class: "btn-oneTeg",
            size: "small",
            onClick: _cache[7] || (_cache[7] = ($event: any) => (onCreateTable(true)))
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_unref(t)("DataStore.CreateAndImport")), 1)
            ]),
            _: 1
          }),
          _createElementVNode("div", _hoisted_18, [
            _createVNode(_unref(ElButton), {
              class: "btn-oneTeg3",
              size: "small",
              onClick: _cache[8] || (_cache[8] = ($event: any) => (createDialogVisible.value = false))
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_unref(t)("Common.Cancel")), 1)
              ]),
              _: 1
            }),
            _createVNode(_unref(ElButton), {
              id: "datastore-create-confirm",
              class: "btn-oneTeg2",
              size: "small",
              onClick: _cache[9] || (_cache[9] = ($event: any) => (onCreateTable(false)))
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_unref(t)("Common.Create")), 1)
              ]),
              _: 1
            })
          ])
        ])
      ]),
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_14, [
          _createElementVNode("label", null, [
            _createElementVNode("strong", null, [
              _createTextVNode(_toDisplayString(_unref(t)("Common.Name")), 1),
              _hoisted_15
            ])
          ]),
          _createVNode(_unref(ElInput), {
            id: "datastore-new-table-name",
            modelValue: newTableName.value,
            "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((newTableName).value = $event)),
            size: "small",
            placeholder: _unref(t)('DataStore.TableNamePlaceholder')
          }, null, 8, ["modelValue", "placeholder"])
        ]),
        _createElementVNode("div", _hoisted_16, [
          _createElementVNode("label", null, [
            _createElementVNode("strong", null, _toDisplayString(_unref(t)("DataStore.Description")), 1)
          ]),
          _createVNode(_unref(ElInput), {
            id: "datastore-new-table-desc",
            modelValue: newTableDescription.value,
            "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((newTableDescription).value = $event)),
            size: "small"
          }, null, 8, ["modelValue"])
        ])
      ]),
      _: 1
    }, 8, ["modelValue", "title"]),
    _createVNode(_unref(ElDialog), {
      modelValue: renameDialogVisible.value,
      "onUpdate:modelValue": _cache[13] || (_cache[13] = ($event: any) => ((renameDialogVisible).value = $event)),
      title: _unref(t)('DataStore.RenameTable'),
      width: "440px",
      "custom-class": "datastore-dialog",
      "append-to-body": "",
      "destroy-on-close": "",
      "close-on-click-modal": false
    }, {
      footer: _withCtx(() => [
        _createVNode(_unref(ElButton), {
          class: "btn-oneTeg3",
          size: "small",
          onClick: _cache[12] || (_cache[12] = ($event: any) => (renameDialogVisible.value = false))
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_unref(t)("Common.Cancel")), 1)
          ]),
          _: 1
        }),
        _createVNode(_unref(ElButton), {
          id: "datastore-rename-confirm",
          class: "btn-oneTeg2",
          size: "small",
          onClick: onRenameTable
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_unref(t)("DataStore.Rename")), 1)
          ]),
          _: 1
        })
      ]),
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_19, [
          _createElementVNode("label", _hoisted_20, [
            _createElementVNode("strong", null, [
              _createTextVNode(_toDisplayString(_unref(t)("Common.Name")), 1),
              _hoisted_21
            ]),
            _createVNode(_unref(ElTooltip), {
              effect: "custom",
              placement: "top",
              content: _unref(t)('DataStore.RenameHint')
            }, {
              default: _withCtx(() => [
                _createVNode(_component_font_awesome_icon, {
                  icon: "info-circle",
                  class: "datastore-info-icon"
                })
              ]),
              _: 1
            }, 8, ["content"])
          ]),
          _createVNode(_unref(ElInput), {
            id: "datastore-rename-input",
            modelValue: renameValue.value,
            "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((renameValue).value = $event)),
            size: "small"
          }, null, 8, ["modelValue"])
        ])
      ]),
      _: 1
    }, 8, ["modelValue", "title"]),
    _withDirectives(_createElementVNode("div", _hoisted_22, [
      _createElementVNode("img", {
        src: `${_unref(ASSETS_BASE_URL)}/${_unref(DEFAULT_LOGO)}.png`,
        alt: "Loading...",
        class: "loader-logo-40"
      }, null, 8, _hoisted_23)
    ], 512), [
      [_vShow, loading.value]
    ])
  ]))
}
}

})